import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { alpha, Box, Button, ClickAwayListener, Grow, List, Paper, Popper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ResponsiveEllipsis } from "..";
import WorkspaceContainer from "./WorkspaceContainer";

const useStyles = makeStyles(theme => ({
  root: {
    marginRight: "auto",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  wrapper: {
    position: "relative",
    minWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      "& .MuiButton-label p": {
        fontSize: 12,
      },
    },
  },
  buttonWorkspace: {
    position: "relative",
    justifyContent: "space-between",
    color: "#66615b",
    backgroundColor: theme.palette.background.default,
    border: "1px solid #c4c4c4",
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 7,
    paddingBottom: 7,
    width: "100%",
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.common.grey,
    },
    "& svg": {
      position: "absolute",
      right: 16,
      width: 20,
    },
    [theme.breakpoints.down("sm")]: {
      padding: "7px 10px",
      fontSize: 12,
      "& svg": {
        right: 2,
      },
    },
    fontSize: 12,
  },
  popper: {
    //minWidth: "inherit",
    //width: "max-content",
    width: "100%",
    zIndex: 100,
  },
  paper: {
    borderRadius: 4,
    boxShadow: "1px 2px 7px 1px rgba(0, 0, 0, 0.125)",
    maxHeight: 400,
    overflowY: "auto",
    "& .MuiList-padding": {
      [theme.breakpoints.down("sm")]: {
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },
  ellipsisText: {
    maxWidth: "80%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));

const WorkspaceSelector = ({
  workspaces,
  workspaceName,
  onClick,
  handleToggle,
  open,
  setOpen,
  searchable,
  searchAll,
}) => {
  const classes = useStyles();

  const [lines, setLines] = React.useState(1);

  const anchorRef = React.useRef(null);

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  useEffect(() => {
    if (workspaceName) {
      const strLine = workspaceName.split("\n");
      setLines(strLine.length);
    }
  }, [workspaceName]);

  return (
    <Box className={classes.root}>
      <Box className={classes.wrapper}>
        <Button
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          className={classes.buttonWorkspace}
        >
          {lines > 1 && (
            <ResponsiveEllipsis
              text={workspaceName}
              maxLine="1"
              ellipsis="..."
              trimRight
              basedOn="words"
            />
          )}
          {lines === 1 && (
            <div className={classes.ellipsisText}>
              {workspaceName || <FormattedMessage id="btn.workspace" />}
            </div>
          )}
          <ArrowDropDown className={classes.caret} />
        </Button>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          className={classes.popper}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "left top" : "left bottom",
              }}
            >
              <Paper id="menu-list-workspaces" className={classes.paper}>
                <ClickAwayListener onClickAway={handleClose}>
                  <List component="nav" aria-labelledby="nested-list-subheader">
                    <WorkspaceContainer
                      handleToggle={handleToggle}
                      workspaces={workspaces}
                      onClick={onClick}
                      searchable={searchable}
                      searchAll={searchAll}
                    />
                  </List>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    </Box>
  );
};

WorkspaceSelector.defaultProps = {
  workspaceName: null,
};

WorkspaceSelector.propTypes = {
  workspaceName: PropTypes.string,
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }).isRequired
  ).isRequired,
  onClick: PropTypes.func.isRequired,
  handleToggle: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default WorkspaceSelector;
